"use client";

import React, { useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Briefcase, 
  FileText, 
  DollarSign, 
  TrendingUp,
  Award,
  ListChecks,
  CalendarClock
} from 'lucide-react';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useVendorDashboardData } from '@/hooks/useVendorDashboardData';
import { useClientPendingActions } from '@/hooks/useClientPendingActions';
import { supabase } from '@/integrations/supabase/client';
import { JobStatusTable } from '@/components/dashboard/JobStatusTable';
import VendorJobStatusTable from '@/components/dashboard/VendorJobStatusTable';
import { UpcomingMilestones } from '@/components/dashboard/UpcomingMilestones';
import { PendingActionsCard } from '@/components/dashboard/PendingActionsCard';
import { PendingActionsModal } from '@/components/dashboard/PendingActionsModal';
import { VendorOnboardingBanner } from '@/components/vendor/VendorOnboardingBanner';
import { useVendorOnboardingStatus } from '@/hooks/useVendorOnboardingStatus';
import { ClientProfileCompletionBanner } from '@/components/client/ClientProfileCompletionBanner';
import { useClientOnboardingStatus } from '@/hooks/useClientOnboardingStatus';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

const StatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  change, 
  changeType = 'neutral' 
}: { 
  title: string; 
  value: string; 
  icon: any; 
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
}) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between space-y-0 pb-2">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="space-y-1">
        <p className="text-2xl font-bold">{value}</p>
        {change && (
          <p className={`text-xs ${
            changeType === 'positive' ? 'text-success' : 
            changeType === 'negative' ? 'text-destructive' : 
            'text-muted-foreground'
          }`}>
            {change}
          </p>
        )}
      </div>
    </CardContent>
  </Card>
);

const ClientDashboard = () => {
  const { user, organization } = useAuth();
  const dashboardData = useDashboardData();
  const pendingActions = useClientPendingActions();
  const { data: onboardingStatus } = useClientOnboardingStatus();
  const [firstName, setFirstName] = React.useState<string>('');
  const [pendingActionsModalOpen, setPendingActionsModalOpen] = React.useState(false);

  // Track dashboard visit on mount
  useEffect(() => {
    analytics.track({
      name: ANALYTICS_EVENTS.DASHBOARD_VISITED,
      category: 'engagement',
      properties: {
        dashboard_type: 'client',
      },
    });
  }, []);

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: dashboardData.currency,
    }).format(cents / 100);
  };

  const activeJobs = 
    dashboardData.jobCountsByStatus.rfq_open +
    dashboardData.jobCountsByStatus.awarded +
    dashboardData.jobCountsByStatus.in_progress +
    dashboardData.jobCountsByStatus.completed_pending_review +
    dashboardData.jobCountsByStatus.accepted +
    dashboardData.jobCountsByStatus.rejected +
    dashboardData.jobCountsByStatus.disputed;
  const inProgressCount = dashboardData.jobCountsByStatus.awarded + 
    dashboardData.jobCountsByStatus.in_progress + 
    dashboardData.jobCountsByStatus.completed_pending_review;
  const completedCount = dashboardData.jobCountsByStatus.accepted + 
    dashboardData.jobCountsByStatus.closed;

  React.useEffect(() => {
    const fetchFirstName = async () => {
      if (user?.id) {
        const { data } = await supabase
          .from('client_profiles')
          .select('first_name')
          .eq('user_id', user.id)
          .single();
        
        if (data?.first_name) {
          setFirstName(data.first_name);
        }
      }
    };
    fetchFirstName();
  }, [user?.id]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Welcome back, {firstName || user?.email.split('@')[0]}!</h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Here's what's happening with your procurement activities at {organization?.name}
        </p>
      </div>

      {/* Client Profile Completion Banner */}
      {onboardingStatus && (
        <ClientProfileCompletionBanner
          needsOnboarding={onboardingStatus.needsOnboarding}
          profileCompletionPercentage={onboardingStatus.profileCompletionPercentage}
          userProfileComplete={onboardingStatus.userProfileComplete}
          companyProfileComplete={onboardingStatus.companyProfileComplete}
          missingItems={onboardingStatus.missingItems}
        />
      )}

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <PendingActionsCard
          count={pendingActions.totalCount}
          loading={pendingActions.loading}
          onClick={() => setPendingActionsModalOpen(true)}
        />
        <StatCard
          title="Active Jobs"
          value={activeJobs.toString()}
          icon={Briefcase}
          change={`Excludes draft & closed jobs`}
          changeType="neutral"
        />
        <StatCard
          title="Value in Progress"
          value={formatCurrency(dashboardData.valueInProgress)}
          icon={TrendingUp}
          change={`${inProgressCount} ${inProgressCount === 1 ? 'job' : 'jobs'} in progress`}
          changeType="neutral"
        />
        <StatCard
          title="Value Completed"
          value={formatCurrency(dashboardData.valueCompleted)}
          icon={DollarSign}
          change={`${completedCount} ${completedCount === 1 ? 'job' : 'jobs'} completed`}
          changeType="neutral"
        />
      </div>

      {/* Pending Actions Modal */}
      <PendingActionsModal
        open={pendingActionsModalOpen}
        onOpenChange={setPendingActionsModalOpen}
        categories={pendingActions.categories}
        totalCount={pendingActions.totalCount}
        onRefresh={pendingActions.refetch}
        loading={pendingActions.loading}
      />

      {/* Dashboard Content */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <ListChecks className="h-5 w-5" />
              <span>Job Status Overview</span>
            </CardTitle>
            <CardDescription>
              Current status of all your procurement jobs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <JobStatusTable data={dashboardData.jobCountsByStatus} loading={dashboardData.loading} />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CalendarClock className="h-5 w-5" />
              <span>Upcoming Milestones</span>
            </CardTitle>
            <CardDescription>
              Next 5 milestones requiring attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            <UpcomingMilestones milestones={dashboardData.upcomingMilestones} loading={dashboardData.loading} />
          </CardContent>
        </Card>
      </div>

    </div>
  );
};

const VendorDashboard = () => {
  const { user, organization } = useAuth();
  const dashboardData = useVendorDashboardData();
  const { data: onboardingStatus } = useVendorOnboardingStatus();
  const [firstName, setFirstName] = React.useState<string>('');

  // Track dashboard visit on mount
  useEffect(() => {
    analytics.track({
      name: ANALYTICS_EVENTS.DASHBOARD_VISITED,
      category: 'engagement',
      properties: {
        dashboard_type: 'vendor',
      },
    });
  }, []);

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: dashboardData.currency,
    }).format(cents / 100);
  };

  React.useEffect(() => {
    const fetchFirstName = async () => {
      if (user?.id) {
        const { data } = await supabase
          .from('vendor_profiles_enhanced')
          .select('first_name')
          .eq('user_id', user.id)
          .single();
        
        if (data?.first_name) {
          setFirstName(data.first_name);
        }
      }
    };
    fetchFirstName();
  }, [user?.id]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
          Welcome back, {firstName || user?.email?.split('@')[0]}!
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Here's what's happening with your jobs at {organization?.name}
        </p>
      </div>

      {/* Vendor Onboarding Banner */}
      {onboardingStatus && (
        <VendorOnboardingBanner
          hasBasicVerification={onboardingStatus.hasBasicVerification}
          hasRequestedVerification={onboardingStatus.hasRequestedVerification}
          stripeConfigured={onboardingStatus.stripeConfigured}
          hasSubmittedBid={onboardingStatus.hasSubmittedBid}
          profileCompletionPercentage={onboardingStatus.profileCompletionPercentage}
          missingItems={onboardingStatus.missingItems}
        />
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Bids"
          value={dashboardData.activeBidsCount.toString()}
          icon={FileText}
          change={`${dashboardData.activeBidsCount} pending ${dashboardData.activeBidsCount === 1 ? 'bid' : 'bids'}`}
          changeType="neutral"
        />
        <StatCard
          title="Won Jobs"
          value={dashboardData.wonJobsCount.toString()}
          icon={Award}
          change={`${dashboardData.wonJobsCount} ${dashboardData.wonJobsCount === 1 ? 'job' : 'jobs'} awarded`}
          changeType="neutral"
        />
        <StatCard
          title="Total Earned"
          value={formatCurrency(dashboardData.totalEarned)}
          icon={DollarSign}
          change={`${dashboardData.jobCountsByStatus.accepted + dashboardData.jobCountsByStatus.closed} ${dashboardData.jobCountsByStatus.accepted + dashboardData.jobCountsByStatus.closed === 1 ? 'job' : 'jobs'} completed`}
          changeType="neutral"
        />
        <StatCard
          title="Win Rate"
          value={`${dashboardData.winRate.toFixed(1)}%`}
          icon={TrendingUp}
          change={dashboardData.winRate > 0 ? "Competitive performance" : "No data available"}
          changeType={dashboardData.winRate > 30 ? "positive" : "neutral"}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <ListChecks className="h-5 w-5" />
              <span>Job Status Overview</span>
            </CardTitle>
            <CardDescription>
              Current status of your awarded jobs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <VendorJobStatusTable 
              data={dashboardData.jobCountsByStatus} 
              loading={dashboardData.loading} 
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CalendarClock className="h-5 w-5" />
              <span>Upcoming Milestones</span>
            </CardTitle>
            <CardDescription>
              Next 5 milestones requiring attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            <UpcomingMilestones 
              milestones={dashboardData.upcomingMilestones} 
              loading={dashboardData.loading} 
            />
          </CardContent>
        </Card>
      </div>

    </div>
  );
};

export default function DashboardPage() {
  const { user, isLoading } = useAuth();

  // Wait for role to be loaded before rendering role-specific content
  if (isLoading || !user?.role) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Explicit role checks with no default fallback
  if (user.role === 'client') {
    return <ClientDashboard />;
  }
  
  if (user.role === 'vendor') {
    return <VendorDashboard />;
  }

  // Unknown role - should never happen but handle gracefully
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">Access Error</h2>
        <p className="text-muted-foreground">Your account role is not recognized. Please contact support.</p>
      </div>
    </div>
  );
}